import { createContext } from 'react';
import {
	PermissionsConfigurationsByType,
	PresetsByType,
} from '@sharefiledev/sharing-pilet-sdk';
import { Link, LinkSettings } from '@sharefiledev/sharing-pilet-sdk/api';
import {
	ConditionalLinkOptionsModalContentType,
	DEFAULT_EXPIRATION_DAYS,
} from '../constants';
import {
	CreateAndReplaceLinkProps,
	CreateConditionalLinkProps,
	UpdateConditionalLinksParams,
} from './LinkBasedShareContextProvider';
import { WatermarkConfig } from './WatermarkEvents';

interface LinkBasedShareContext {
	setShareWithSpecificPeopleOpen(value: boolean): void;
	isShareWithPeopleModalOpen: boolean;
	parentContainerRid: string;
	linkRid: string;
	linkUrl: string;
	conditionalLinks: Link[];
	setConditionalLinks: (links: Link[]) => void;
	isLinkBasedManageUserModalOpen?: boolean;
	setIsLinkBasedManageUserModalOpen: (isModalOpen: boolean) => void;
	createLink(): void;
	expiryDate: number;
	setExpiryDate: (value: number) => void;
	latestVersionFlag: boolean;
	setLatestVersionFlag: (value: boolean) => void;
	waterMarkFlag: boolean;
	setWaterMarkFlag: (value: boolean) => void;
	notifyMeFlag: boolean;
	setNotifyMeFlag: (value: boolean) => void;
	createConditionalLink(conditionalLinkOptions: CreateConditionalLinkProps): void;
	createAndReplaceLink(createAndReplaceLinkProps: CreateAndReplaceLinkProps): void;
	createLinkButtonLoading: boolean;
	isViewConditionalOptionModalOpen: boolean;
	setIsViewConditionalOptionModalOpen: (value: boolean) => void;
	linkOptionsContentType: ConditionalLinkOptionsModalContentType;
	setLinkOptionsContentType: (value: ConditionalLinkOptionsModalContentType) => void;
	updateConditionalLinks(params: UpdateConditionalLinksParams): void;
	consumerPluginName?: string;
	isManageUserTriggeredByExtensionSlot?: boolean;
	accountLevelLinkSettings: LinkSettings | undefined;
	configurationsByTypeForWatermarking: PermissionsConfigurationsByType;
	sharablePresetsByTypeForWatermarking: PresetsByType[];
	watermarkedResponse: WatermarkConfig | undefined;
	isWatermarkingAvailable: boolean;
}

const throwUnmounted: any = () => {
	throw new Error('Unmounted context');
};

const defaultLinkBasedShareContext: LinkBasedShareContext = {
	setShareWithSpecificPeopleOpen: throwUnmounted,
	isShareWithPeopleModalOpen: false,
	parentContainerRid: '',
	linkRid: '',
	linkUrl: '',
	conditionalLinks: [],
	setConditionalLinks: throwUnmounted,
	isLinkBasedManageUserModalOpen: false,
	setIsLinkBasedManageUserModalOpen: throwUnmounted,
	createLink: throwUnmounted,
	expiryDate: DEFAULT_EXPIRATION_DAYS,
	setExpiryDate: throwUnmounted,
	latestVersionFlag: false,
	setLatestVersionFlag: throwUnmounted,
	waterMarkFlag: false,
	setWaterMarkFlag: throwUnmounted,
	notifyMeFlag: false,
	setNotifyMeFlag: throwUnmounted,
	createConditionalLink: throwUnmounted,
	createAndReplaceLink: throwUnmounted,
	createLinkButtonLoading: false,
	isViewConditionalOptionModalOpen: false,
	setIsViewConditionalOptionModalOpen: throwUnmounted,
	linkOptionsContentType: ConditionalLinkOptionsModalContentType.None,
	setLinkOptionsContentType: throwUnmounted,
	updateConditionalLinks: throwUnmounted,
	consumerPluginName: '',
	isManageUserTriggeredByExtensionSlot: false,
	accountLevelLinkSettings: {},
	configurationsByTypeForWatermarking: {
		availablePermissionSetsByType: {},
	},
	sharablePresetsByTypeForWatermarking: [],
	watermarkedResponse: {
		transformedRids: [],
		success: false,
		labels: [],
		pluginName: '',
	},
	isWatermarkingAvailable: false,
};

export const LinkBasedShareContext = createContext<LinkBasedShareContext>(
	defaultLinkBasedShareContext
);
